import { MaskedField, Text } from "@simplybusiness/mobius";
import type { QuestionComponentProps } from "../../types";
import { useLocale } from "../../../../../../../hooks/useLocale";
import type React from "react";

// Remove commas from pre-formatted values, return as string
export const sanitizeValue = (
  val: string | number | undefined,
): string | undefined => {
  if (val === undefined) return undefined;
  else if (typeof val === "number") return val.toString();
  // Remove commas (thousands separators)
  return val.replace(/,/g, "");
};

// Convert string to number, treating empty strings as 0
const stringToNumber = (value: string): number => {
  return value === "" ? 0 : Number(value);
};

export const CurrencyQuestion = ({
  questionFieldProps,
  config,
}: QuestionComponentProps) => {
  const {
    value,
    validation,
    readOnly,
    onChange,
    onBlur,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const { isUK } = useLocale();

  const sanitizedValue = sanitizeValue(value as string | number | undefined);
  const { wholeNumber = false } = config ?? {};
  const symbol = isUK ? "£" : "$";
  const mask = {
    mask: Number,
    thousandsSeparator: ",",
    scale: wholeNumber ? 0 : 2,
    signed: false,
  };

  // Convert string to number for onChange events
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange?.({
      target: {
        name: event.target.name,
        value: stringToNumber(event.target.value),
      },
    });

  // Convert string to number for onBlur events
  const handleBlur = (event: React.FocusEvent<Element>) =>
    onBlur?.({
      ...event,
      target: {
        name: (event.target as HTMLInputElement).name,
        value: stringToNumber((event.target as HTMLInputElement).value),
      },
    });

  return (
    <MaskedField
      {...otherProps}
      onChange={handleChange}
      onBlur={handleBlur}
      mask={mask}
      prefixInside={<Text>{symbol}</Text>}
      isReadOnly={!!readOnly}
      defaultValue={sanitizedValue}
      useMaskedValue={false} // Emit "1234.56" (not "1,234.56")
      inputMode={wholeNumber ? "numeric" : "decimal"}
    />
  );
};
